import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Coins, Sparkles, Check, Loader2, TrendingUp, ArrowUpRight } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

/**
 * Credits page — buy prepaid credits for Premium AI generation.
 *
 * Flow:
 *  1. User clicks a pack → POST /api/credits/checkout → redirect to Stripe
 *  2. Stripe redirects back with ?session_id=…
 *  3. We poll /api/credits/status/:session_id every 2s (max 8 attempts)
 *  4. When paid, balance auto-refreshes and history reloads
 *
 * Pricing is fetched from the backend so it can be tweaked without a deploy.
 */
export default function Credits() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState({
    balance: 0,
    creditsPerImage: 5,
    packs: [],
  });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);      // pack_id currently being purchased
  const [polling, setPolling] = useState(null);    // session_id being polled

  const load = useCallback(async () => {
    try {
      const [packsResp, histResp] = await Promise.all([
        api.get("/credits/packs"),
        api.get("/credits/history"),
      ]);
      setState({
        balance: packsResp.data.balance,
        creditsPerImage: packsResp.data.credits_per_image,
        packs: packsResp.data.packs,
      });
      setHistory(histResp.data.transactions || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // If Stripe redirected us back with a session_id, poll for completion.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPolling(sessionId);
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/credits/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          toast.success(`+${data.credits_added} credits added — you now have ${data.balance}.`);
          setPolling(null);
          // Strip session_id from URL so a refresh doesn't re-poll a paid session.
          navigate("/dashboard/credits", { replace: true });
          await load();
          if (refresh) await refresh();
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired — please try again.");
          setPolling(null);
          navigate("/dashboard/credits", { replace: true });
          return;
        }
        if (attempts >= 8) {
          toast.info("Still processing — your credits will appear once Stripe confirms the payment.");
          setPolling(null);
          navigate("/dashboard/credits", { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (cancelled) return;
        toast.error(formatApiError(err));
        setPolling(null);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [location.search, navigate, load, refresh]);

  const buyPack = async (packId) => {
    setBuying(packId);
    try {
      const { data } = await api.post("/credits/checkout", {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBuying(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const packTone = (idx) => {
    if (idx === 0) return { border: "#E2E8F0", bg: "#FFFFFF", accent: "#0EA5E9" };
    if (idx === 1) return { border: "#C4B5FD", bg: "linear-gradient(135deg, #FAF5FF, #EEF2FF)", accent: "#8B5CF6" };
    return { border: "#FDBA74", bg: "linear-gradient(135deg, #FFF7ED, #FEF3C7)", accent: "#EA580C" };
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-6xl text-slate-900" data-testid="credits-page">
        {/* Header */}
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-violet-600 mb-3">Credits</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
            Premium AI{" "}
            <span style={accentFont} className="text-violet-600">on demand</span>.
          </h1>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl">
            Buy credits once, spend them whenever you need ChatGPT-quality images. Every Premium generation
            (GPT Image 1, or any image-to-image blend) costs <strong className="text-slate-800">{state.creditsPerImage} credits</strong>.
            Nano Banana text-to-image stays free.
          </p>
        </div>

        {/* Balance card */}
        <div
          className="rounded-3xl p-6 md:p-8 mb-8 border relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #0F172A, #1E1B4B)", borderColor: "#4C1D95" }}
          data-testid="credits-balance-card"
        >
          <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full opacity-40" style={{ background: "radial-gradient(circle, #8B5CF6, transparent 70%)" }} />
          <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-violet-300 mb-2 font-medium">Current balance</div>
              <div className="flex items-baseline gap-3">
                <Coins className="h-9 w-9 text-amber-300" />
                <div className="text-6xl md:text-7xl font-semibold text-white tracking-tight" style={headingFont} data-testid="credits-balance-amount">
                  {loading ? "—" : state.balance.toLocaleString()}
                </div>
                <div className="text-sm text-violet-200">credits</div>
              </div>
              <div className="text-sm text-violet-200 mt-2">
                Enough for <strong className="text-white">{Math.floor((state.balance || 0) / (state.creditsPerImage || 5))}</strong> Premium image{Math.floor((state.balance || 0) / (state.creditsPerImage || 5)) === 1 ? "" : "s"}.
              </div>
            </div>
            {polling && (
              <div className="text-xs text-violet-200 flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5" data-testid="credits-polling-status">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Confirming payment with Stripe…
              </div>
            )}
          </div>
        </div>

        {/* Packs */}
        <div className="mb-2 text-xs uppercase tracking-[0.25em] text-slate-500 font-medium">Top up</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {state.packs.map((p, i) => {
            const tone = packTone(i);
            const isMid = i === 1;
            const isBuying = buying === p.id;
            return (
              <div
                key={p.id}
                className="rounded-3xl p-6 border flex flex-col relative overflow-hidden"
                style={{
                  background: tone.bg,
                  borderColor: tone.border,
                  boxShadow: isMid ? "0 12px 40px rgba(139,92,246,0.20)" : "0 6px 22px rgba(15,23,42,0.06)",
                }}
                data-testid={`credit-pack-${p.id}`}
              >
                {isMid && (
                  <div className="absolute top-0 right-0 px-3 py-1 text-[9px] uppercase tracking-[0.2em] font-bold text-white rounded-bl-2xl" style={{ background: tone.accent }}>
                    Best value
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.25em] font-semibold mb-1" style={{ color: tone.accent }}>
                  {p.label}
                </div>
                <div className="flex items-baseline gap-1 mb-1" style={headingFont}>
                  <div className="text-5xl tracking-tight text-slate-900">${p.amount_usd.toFixed(0)}</div>
                  <div className="text-xs text-slate-500 ml-1">one-time</div>
                </div>
                <div className="flex items-baseline gap-2 mt-2 mb-3">
                  <Coins className="h-4 w-4" style={{ color: tone.accent }} />
                  <div className="text-2xl font-semibold text-slate-900" data-testid={`credit-pack-${p.id}-credits`}>
                    {p.credits.toLocaleString()}
                  </div>
                  <div className="text-xs text-slate-500">credits</div>
                  {p.credits_bonus > 0 && (
                    <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${tone.accent}22`, color: tone.accent }}>
                      +{p.bonus_pct}% bonus
                    </span>
                  )}
                </div>
                <ul className="space-y-1.5 text-sm text-slate-700 mb-6">
                  <li className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: tone.accent }} /><span><strong>{p.images_included}</strong> premium images (GPT Image 1)</span></li>
                  <li className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: tone.accent }} /><span>Same credits work for image-to-image blends</span></li>
                  <li className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: tone.accent }} /><span>No expiry — credits never rot</span></li>
                  {p.credits_bonus > 0 && (
                    <li className="flex gap-2"><TrendingUp className="h-4 w-4 mt-0.5 shrink-0" style={{ color: tone.accent }} /><span><strong>+{p.credits_bonus}</strong> bonus credits</span></li>
                  )}
                </ul>
                <Button
                  onClick={() => buyPack(p.id)}
                  disabled={isBuying || buying !== null}
                  className="rounded-full mt-auto h-11 text-white font-semibold"
                  style={{ background: tone.accent }}
                  data-testid={`credit-pack-${p.id}-buy`}
                >
                  {isBuying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  {isBuying ? "Redirecting…" : `Buy ${p.credits} credits`}
                </Button>
                <div className="text-[10px] text-slate-400 text-center mt-2">
                  Powered by Stripe · secure checkout
                </div>
              </div>
            );
          })}
        </div>

        {/* Transaction history */}
        <div className="mb-2 text-xs uppercase tracking-[0.25em] text-slate-500 font-medium">Recent activity</div>
        <div className="rounded-2xl border bg-white overflow-hidden" style={{ borderColor: "#E2E8F0" }} data-testid="credits-history">
          {loading ? (
            <div className="py-10 text-center text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
          ) : history.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="credits-history-empty">
              No credit activity yet — buy a pack above to start creating premium AI images.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold">When</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold">Reason</th>
                  <th className="text-right px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold">Δ Credits</th>
                </tr>
              </thead>
              <tbody>
                {history.map((t) => (
                  <tr key={t.id} className="border-t" style={{ borderColor: "#F1F5F9" }} data-testid={`credit-history-row-${t.id}`}>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(t.created_at)}</td>
                    <td className="px-4 py-3 text-slate-800">
                      {t.type === "purchase" ? "Credit pack purchase" : "AI image generation"}
                      {t.metadata?.provider && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">
                          · {t.metadata.provider === "openai" ? "GPT Image 1" : "Nano Banana"}
                        </span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold ${t.credits >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
                      {t.credits >= 0 ? `+${t.credits}` : t.credits}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="text-xs text-slate-400 mt-6 flex items-center gap-1" data-testid="credits-disclaimer">
          Payments via Stripe · Test mode (card 4242 4242 4242 4242 · any future expiry · any CVC).
          <a href="/dashboard/billing" className="ml-auto text-violet-600 hover:underline flex items-center gap-0.5">
            Manage subscription <ArrowUpRight className="h-3 w-3" />
          </a>
        </div>
      </div>
    </DashboardLayout>
  );
}
